import NiceModal, { useModal } from "@ebay/nice-modal-react";
import { styled, tooltipClasses, TooltipProps } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { memo, useMemo } from "react";
import ReactModal from "react-modal";
import { Link } from "react-router-dom";

import { ReactComponent as LinkIcon } from "assets/icons/link.svg";
import Button from "components/Button";
import Empty from "components/Empty";
import { useClearReports, useGetReports } from "hooks/apis/user";
import format from "lib/format";
import SimpleModal from "./SimpleModal";

const ReportDetail = ({
  id,
  name,
  userId,
}: {
  id: number;
  name: string;
  userId: string;
}) => {
  const modal = useModal();
  const { data, isFetching } = useGetReports({ id });
  const clearReportsMutation = useClearReports();

  const closeModal = () => {
    modal.hide();
    modal.reject();
  };

  const activeReports = useMemo(
    () => data?.filter((r) => r.status === 1),
    [data],
  );

  const onActivate = () => {
    if (data?.length) {
      NiceModal.show(SimpleModal, {
        title: "계정 활성화",
        message: `‘${name}(${userId})’ 계정을 활성화 하시겠습니까?`,
      })
        .then(() => {
          clearReportsMutation.mutate({ id });
        })
        .catch(() => {});
    }
  };

  return (
    <ReactModal
      overlayClassName="overflow-auto flex fixed size-full top-0 left-0 z-30"
      className="bg-[rgba(0,0,0,0.2)] min-w-[1280px] min-h-[720px] flex justify-center items-center size-full outline-none"
      isOpen={modal.visible}
      onRequestClose={closeModal}
      onAfterClose={() => {
        modal.remove();
      }}
    >
      <main className="bg-white flex flex-col w-[50rem] h-[37.5rem] p-[1.875rem] pt-[2.5rem] rounded-md">
        <h2 className="text-center font-bold text-24 leading-[1.375rem]">
          신고 내역
        </h2>
        <section className="flex flex-col flex-1 overflow-hidden pt-[2.875rem] gap-[0.625rem]">
          <span className="text-16 text-secondary">
            총 {data?.length ?? 0}건
          </span>
          <div className="flex-1 flex flex-col overflow-hidden">
            <table className="flex flex-col">
              <thead className="block text-16 text-white">
                <tr className="flex w-full">
                  <th className="h-14 flex justify-center items-center w-[5.875rem] bg-secondary rounded-l-md">
                    번호
                  </th>
                  <th className="h-14 flex items-center flex-1 bg-secondary px-5 text-left">
                    신고유형
                  </th>
                  <th className="h-14 flex justify-center items-center w-[5.875rem] bg-secondary">
                    신고자
                  </th>
                  <th className="h-14 flex justify-center items-center w-[5.875rem] bg-secondary">
                    신고일자
                  </th>
                  <th className="h-14 flex justify-center items-center w-[5.875rem] bg-secondary rounded-r-md">
                    링크
                  </th>
                </tr>
              </thead>
              <tbody className="flex-1 text-center text-secondary font-medium text-16">
                {data?.map((report, index) => (
                  <tr key={report.id} className="flex border-b border-gray">
                    <td className="h-14 w-[5.875rem] flex justify-center items-center">
                      {data.length - index}
                    </td>
                    <td className="h-14 flex flex-1 items-center text-left px-5">
                      {report.content}
                    </td>
                    <td className="h-14 w-[5.875rem] flex justify-center items-center">
                      <div className="flex flex-col items-center">
                        <span>{report?.user?.name}</span>
                        <span className="text-12">
                          {report?.user?.userId
                            ? `(${report?.user?.userId})`
                            : ""}
                        </span>
                      </div>
                    </td>
                    <td className="h-14 w-[5.875rem] flex justify-center items-center">
                      {format(report.createdAt, "yyyy/MM/dd")}
                    </td>
                    <td className="h-14 w-[5.875rem] flex justify-center items-center">
                      <Link to={`/qna/${report.answer?.questionBNo}`}>
                        <LinkIcon className="size-6 mx-auto" />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Empty
              show={!isFetching && !data?.length}
              message="신고가 없습니다."
            />
          </div>
          <div className="flex justify-end gap-2 !mt-[1.875rem]">
            <Button className="w-[10.688rem]" outline onClick={closeModal}>
              취소
            </Button>
            {(activeReports?.length || 0) < 3 ? (
              <ButtonTooltip title="이 계정은 비활성화되지 않았습니다.">
                <span>
                  <Button disabled className="w-[10.688rem]">
                    계정 활성화
                  </Button>
                </span>
              </ButtonTooltip>
            ) : (
              <Button onClick={onActivate} className="w-[10.688rem]">
                계정 활성화
              </Button>
            )}
          </div>
        </section>
      </main>
    </ReactModal>
  );
};

const ButtonTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} placement="top" arrow />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "black",
    color: "white",
    fontSize: theme.typography.pxToRem(13),
    padding: "3px 6px",
    marginLeft: "0.688rem!important",
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "black",
  },
}));

const ReportDetailModal = NiceModal.create(memo(ReportDetail));
ReportDetailModal.displayName = "ReportDetailModal";

export default ReportDetailModal;
